<template>
  <BaseModal
    :is-visible="isVisible"
    title="Upload Staff Documents"
    :subtitle="employee ? `Upload files & credentials for ${employee.first_name} ${employee.last_name}` : 'Upload employee documents'"
    :icon="UploadCloud"
    size="3xl"
    type="default"
    :show-footer="true"
    @close="closeModal"
  >
    <form @submit.prevent="handleUpload" id="uploadStaffDocsForm" class="space-y-5">
      <!-- Admin Notice for Paper Records -->
      <div class="p-3.5 rounded-xl bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200/80 dark:border-blue-900/40 flex items-start gap-3 text-xs text-blue-800 dark:text-blue-300">
        <Info class="w-4 h-4 flex-shrink-0 text-blue-500 mt-0.5" />
        <div>
          <span class="font-semibold">Multiple Files & Categorization:</span>
          You can upload multiple files at once and assign different categories (e.g. License, CPR Certificate, Paper Application Record) and expiration dates to each individual file below.
        </div>
      </div>

      <!-- Drag & Drop Zone -->
      <div>
        <div
          class="border-2 border-dashed rounded-2xl p-6 text-center transition-all duration-200 cursor-pointer"
          :class="[
            isDragging
              ? 'border-secondary bg-secondary/5 dark:bg-secondary-dark/10 scale-[1.01]'
              : 'border-gray-300 dark:border-gray-700 hover:border-secondary/60 bg-gray-50/50 dark:bg-gray-800/40'
          ]"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop"
          @click="triggerFileInput"
        >
          <input
            ref="fileInputRef"
            type="file"
            multiple
            class="hidden"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            @change="handleFileInput"
          />

          <div class="flex flex-col items-center justify-center space-y-2">
            <div class="w-12 h-12 rounded-2xl bg-secondary/10 dark:bg-secondary-dark/20 flex items-center justify-center text-secondary dark:text-secondary-dark-text">
              <UploadCloud class="w-6 h-6" />
            </div>
            <div>
              <p class="text-sm font-semibold text-gray-800 dark:text-gray-200">
                <span class="text-secondary dark:text-secondary-dark-text underline underline-offset-2">Click to browse</span>
                or drag & drop files here
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Accepted: PDF, DOC, DOCX, PNG, JPG (Max 32MB per file) • Select one or multiple files
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Error banner -->
      <div v-if="fileError" class="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400 flex items-center justify-between">
        <span>{{ fileError }}</span>
        <button type="button" @click="fileError = ''" class="text-red-500 hover:text-red-700">
          <X class="w-3.5 h-3.5" />
        </button>
      </div>

      <!-- Batch Category Selector Helper (Only shown when files are added) -->
      <div v-if="fileItems.length > 1" class="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3 text-xs">
        <div class="flex items-center gap-2">
          <Layers class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
          <span class="font-semibold text-gray-700 dark:text-gray-300">Set category for all {{ fileItems.length }} files:</span>
        </div>
        <div class="flex items-center gap-2">
          <select
            v-model="batchCategory"
            class="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-1 focus:ring-secondary"
          >
            <option value="" disabled>Select category</option>
            <option
              v-for="cat in DOCUMENT_CATEGORIES"
              :key="cat.value"
              :value="cat.value"
            >
              {{ cat.label }}
            </option>
          </select>
          <button
            type="button"
            @click="applyBatchCategory"
            class="px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-secondary text-white hover:bg-secondary-dark transition"
          >
            Apply to All
          </button>
        </div>
      </div>

      <!-- File Queue List with Per-File Category, Title, Expiry & Notes -->
      <div v-if="fileItems.length > 0" class="space-y-3">
        <div class="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          <span>Configured Documents ({{ fileItems.length }})</span>
          <button
            type="button"
            @click="clearAllFiles"
            class="text-red-500 hover:text-red-700 normal-case font-medium text-xs transition"
          >
            Remove All
          </button>
        </div>

        <div class="space-y-3 max-h-96 overflow-y-auto pr-1">
          <div
            v-for="(item, index) in fileItems"
            :key="item.id"
            class="p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xs space-y-3"
          >
            <!-- Card Header: File Info + Remove Action -->
            <div class="flex items-center justify-between pb-2 border-b border-gray-100 dark:border-gray-700/60">
              <div class="flex items-center gap-2.5 min-w-0">
                <div class="p-2 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex-shrink-0">
                  <FileText v-if="isPdf(item.file)" class="w-4 h-4 text-red-500" />
                  <FileText v-else-if="isDoc(item.file)" class="w-4 h-4 text-blue-500" />
                  <ImageIcon v-else-if="isImage(item.file)" class="w-4 h-4 text-emerald-500" />
                  <Paperclip v-else class="w-4 h-4 text-gray-500" />
                </div>
                <div class="min-w-0">
                  <p class="text-xs font-bold text-gray-900 dark:text-white truncate" :title="item.file.name">
                    {{ item.file.name }}
                  </p>
                  <p class="text-[11px] text-gray-500 dark:text-gray-400">
                    {{ formatFileSize(item.file.size) }}
                  </p>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <span
                  v-if="item.category"
                  :class="[
                    'px-2 py-0.5 rounded-lg text-[10px] font-bold border hidden sm:inline-block',
                    getCategoryBadgeClass(item.category)
                  ]"
                >
                  {{ getCategoryLabel(item.category) }}
                </span>
                <button
                  type="button"
                  @click="removeFile(index)"
                  class="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition"
                  title="Remove this file"
                >
                  <Trash2 class="w-4 h-4" />
                </button>
              </div>
            </div>

            <!-- Card Body: Specific Category, Document Name, Expiration & Notes -->
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <!-- Category Selector -->
              <div>
                <label class="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Category <span class="text-red-500">*</span>
                </label>
                <select
                  v-model="item.category"
                  class="w-full px-2.5 py-1.5 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-secondary"
                  required
                >
                  <option value="" disabled>Select category</option>
                  <option
                    v-for="cat in DOCUMENT_CATEGORIES"
                    :key="cat.value"
                    :value="cat.value"
                  >
                    {{ cat.label }}
                  </option>
                </select>
              </div>

              <!-- Document Title / Custom Name -->
              <div>
                <label class="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Document Title <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="item.name"
                  type="text"
                  placeholder="e.g. Driver's License"
                  class="w-full px-2.5 py-1.5 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-secondary"
                  required
                />
              </div>

              <!-- Expiry Date -->
              <div>
                <label class="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Expiration Date
                </label>
                <input
                  v-model="item.expiry_date"
                  type="date"
                  class="w-full px-2.5 py-1.5 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-secondary"
                />
              </div>

              <!-- Notes / Remarks (Full width or across columns) -->
              <div class="sm:col-span-2 md:col-span-3">
                <label class="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Notes / Description <span class="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  v-model="item.notes"
                  type="text"
                  placeholder="e.g. Scanned paper copy signed on file"
                  class="w-full px-2.5 py-1.5 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-secondary"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Upload Progress Indicator -->
      <div v-if="isUploading" class="space-y-1.5">
        <div class="flex justify-between text-xs font-medium text-gray-700 dark:text-gray-300">
          <span>Uploading documents...</span>
          <span>{{ uploadProgress }}%</span>
        </div>
        <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
          <div
            class="bg-secondary h-2 rounded-full transition-all duration-300"
            :style="{ width: `${uploadProgress}%` }"
          ></div>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <div class="text-xs font-medium text-gray-500 dark:text-gray-400">
          <span v-if="fileItems.length > 0">
            Ready to upload: <span class="font-bold text-gray-900 dark:text-white">{{ fileItems.length }}</span> file(s)
          </span>
        </div>

        <div class="flex items-center gap-3">
          <BaseButton
            text="Cancel"
            variant="outline"
            size="md"
            :disabled="isUploading"
            @click="closeModal"
          />
          <BaseButton
            :text="fileItems.length > 1 ? `Upload ${fileItems.length} Files` : 'Upload Document'"
            variant="primary"
            size="md"
            :loading="isUploading"
            :disabled="fileItems.length === 0"
            :icon="UploadCloud"
            @click="handleUpload"
          />
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import { employeeDocumentService } from '@/services/employeeDocumentService'
import { useToastService } from '@/services/toast'
import type { employee } from '@/types/employee'
import { 
  DOCUMENT_CATEGORIES, 
  type DocumentCategory,
  type EmployeeDocument
} from '@/types/employeeDocument'
import {
  UploadCloud,
  FileText,
  ImageIcon,
  Paperclip,
  Trash2,
  X,
  Info,
  Layers,
} from 'lucide-vue-next'

interface FileUploadItem {
  id: string
  file: File
  name: string
  category: DocumentCategory | ''
  expiry_date: string
  notes: string
}

const props = defineProps<{
  isVisible: boolean
  employee: employee | null
  initialCategory?: DocumentCategory
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'success', uploadedDocs: EmployeeDocument[]): void
}>()

const toast = useToastService()

const fileItems = ref<FileUploadItem[]>([])
const batchCategory = ref<DocumentCategory | ''>(props.initialCategory || '')
const isDragging = ref(false)
const isUploading = ref(false)
const uploadProgress = ref(0)
const fileError = ref('')
const fileInputRef = ref<HTMLInputElement | null>(null)

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      batchCategory.value = props.initialCategory || ''
      fileItems.value = []
      fileError.value = ''
      uploadProgress.value = 0
    }
  }
)

const triggerFileInput = () => {
  fileInputRef.value?.click()
}

const handleFileInput = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files) {
    addFiles(Array.from(target.files))
  }
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

const handleDrop = (e: DragEvent) => {
  isDragging.value = false
  if (e.dataTransfer?.files) {
    addFiles(Array.from(e.dataTransfer.files))
  }
}

const formatCleanTitle = (fileName: string) => {
  const cleanBase = fileName.replace(/\.[^/.]+$/, '')
  return cleanBase
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase())
}

const addFiles = (files: File[]) => {
  fileError.value = ''
  const maxBytes = 32 * 1024 * 1024 // 32MB
  const allowedExtensions = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']

  for (const file of files) {
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    if (!allowedExtensions.includes(ext)) {
      fileError.value = `The uploaded file(s) has an unsupported format. Allowed: PDF, DOC, DOCX, PNG, JPG.`
      return
    }
    if (file.size > maxBytes) {
      fileError.value = `File "${file.name}" exceeds the 32MB size limit.`
      return
    }
    // Prevent duplicate files
    if (!fileItems.value.some(item => item.file.name === file.name && item.file.size === file.size)) {
      fileItems.value.push({
        id: `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        file,
        name: formatCleanTitle(file.name),
        category: batchCategory.value || props.initialCategory || '',
        expiry_date: '',
        notes: '',
      })
    }
  }
}

const applyBatchCategory = () => {
  if (!batchCategory.value) {
    fileError.value = 'Please select a category before applying it to all documents.'
    return
  }

  fileItems.value.forEach(item => {
    item.category = batchCategory.value
  })
  toast.info(`Updated all files to "${getCategoryLabel(batchCategory.value)}"`)
}

const removeFile = (index: number) => {
  fileItems.value.splice(index, 1)
}

const clearAllFiles = () => {
  fileItems.value = []
}

const getCategoryLabel = (category: string) => {
  const item = DOCUMENT_CATEGORIES.find(c => c.value === category)
  return item ? item.label : category
}

const getCategoryBadgeClass = (category: string) => {
  const item = DOCUMENT_CATEGORIES.find(c => c.value === category)
  return item ? item.badgeClass : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const isPdf = (file: File) => file.type === 'application/pdf' || file.name.endsWith('.pdf')
const isDoc = (file: File) => file.name.endsWith('.doc') || file.name.endsWith('.docx')
const isImage = (file: File) => file.type.startsWith('image/') || /\.(jpg|jpeg|png)$/i.test(file.name)

const handleUpload = async () => {
  if (!props.employee || !props.employee.user_id) {
    toast.error('Cannot upload document: Missing employee user ID.')
    return
  }

  if (fileItems.value.length === 0) {
    fileError.value = 'Please select at least one document to upload.'
    return
  }

  const missingCategory = fileItems.value.some(item => !item.category)
  if (missingCategory) {
    fileError.value = 'Please select a category for every document before uploading.'
    return
  }

  const missingTitle = fileItems.value.some(item => !item.name.trim())
  if (missingTitle) {
    fileError.value = 'Please enter a document title for every document before uploading.'
    return
  }

  isUploading.value = true
  uploadProgress.value = 0

  try {
    const formData = new FormData()

    fileItems.value.forEach((item) => {
      formData.append('documents[]', item.file)
      formData.append('document_categories[]', item.category)
      formData.append('document_names[]', item.name.trim())
      formData.append('expiry_dates[]', item.expiry_date || '')
      formData.append('notes[]', item.notes.trim() || '')
    })

    const createdDocs = await employeeDocumentService.uploadDocuments(
      props.employee.user_id,
      formData,
      (event) => {
        if (event.total) {
          uploadProgress.value = Math.round((event.loaded * 100) / event.total)
        }
      }
    )

    toast.success(`${createdDocs.length} document(s) uploaded successfully.`)
    emit('success', createdDocs)
    emit('close')
  } catch (err: any) {
    const msg = err?.response?.data?.message || 'Failed to upload documents. Please try again.'
    toast.error(msg)
  } finally {
    isUploading.value = false
  }
}

const closeModal = () => {
  if (!isUploading.value) {
    emit('close')
  }
}
</script>
